import React, { useState } from "react";
import { useAutoReferralAssignments } from "../api/autoReferral";

interface AutoReferralAssignmentsHistoryProps {
    programId: number;
}

export const AutoReferralAssignmentsHistory: React.FC<
    AutoReferralAssignmentsHistoryProps
> = ({ programId }) => {
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState<{
        new_user_id?: number;
        assigned_inviter_id?: number;
    }>({});

    const {
        data: assignmentsData,
        isLoading,
        refetch,
    } = useAutoReferralAssignments(programId, {
        page,
        per_page: 20,
        ...filters,
    });

    const assignments = assignmentsData?.data ?? [];
    const meta = assignmentsData?.meta;

    const applyFilters = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setPage(1);
        refetch();
    };

    return (
        <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    История назначения рефералов
                </h2>
                <div className="text-sm text-gray-500">
                    Всего назначений: {meta?.total ?? 0}
                </div>
            </div>

            <form
                onSubmit={applyFilters}
                className="grid grid-cols-1 md:grid-cols-4 gap-4"
            >
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        ID нового пользователя
                    </label>
                    <input
                        type="number"
                        value={filters.new_user_id || ""}
                        onChange={(e) =>
                            setFilters((prev) => ({
                                ...prev,
                                new_user_id: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                            }))
                        }
                        className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Необязательно"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        ID пригласителя
                    </label>
                    <input
                        type="number"
                        value={filters.assigned_inviter_id || ""}
                        onChange={(e) =>
                            setFilters((prev) => ({
                                ...prev,
                                assigned_inviter_id: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                            }))
                        }
                        className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Необязательно"
                    />
                </div>
                <div className="flex items-end">
                    <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                        Применить
                    </button>
                </div>
            </form>

            {isLoading ? (
                <div className="text-gray-500 dark:text-gray-300">
                    Загрузка истории назначений...
                </div>
            ) : assignments.length ? (
                <>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Дата
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Новый пользователь
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Пригласитель
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Процент вложений
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Случайное значение
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                {assignments.map((assignment) => (
                                    <tr key={assignment.id}>
                                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                                            {new Date(
                                                assignment.created_at
                                            ).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                                            <div className="font-semibold">
                                                {assignment.new_user?.name ||
                                                    assignment.new_user?.email ||
                                                    `ID: ${assignment.new_user_id}`}
                                            </div>
                                            {assignment.new_user?.email && (
                                                <div className="text-xs text-gray-500">
                                                    {assignment.new_user.email}
                                                </div>
                                            )}
                                            <div className="text-xs text-gray-500">
                                                ID: {assignment.new_user_id}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                                            <div className="font-semibold">
                                                {assignment.assigned_inviter
                                                    ?.name ||
                                                    assignment.assigned_inviter
                                                        ?.email ||
                                                    `ID: ${assignment.assigned_inviter_id}`}
                                            </div>
                                            {assignment.assigned_inviter
                                                ?.email && (
                                                <div className="text-xs text-gray-500">
                                                    {
                                                        assignment
                                                            .assigned_inviter
                                                            .email
                                                    }
                                                </div>
                                            )}
                                            <div className="text-xs text-gray-500">
                                                ID:{" "}
                                                {assignment.assigned_inviter_id}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                                            {assignment.inviter_investment_percent.toFixed(
                                                2
                                            )}
                                            %
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                                            {assignment.random_value.toFixed(8)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {meta && assignments.length > 0 && (
                        <div className="flex items-center justify-between mt-4 text-sm text-gray-600 dark:text-gray-300">
                            <div>
                                Страница {meta.current_page} из{" "}
                                {meta.last_page}. Всего назначений: {meta.total}.
                            </div>
                            <div className="space-x-2">
                                <button
                                    disabled={page <= 1}
                                    onClick={() =>
                                        setPage((prev) => Math.max(1, prev - 1))
                                    }
                                    className={`px-3 py-1 rounded border ${
                                        page <= 1
                                            ? "text-gray-400 border-gray-200 cursor-not-allowed"
                                            : "text-gray-700 border-gray-300 hover:bg-gray-100"
                                    }`}
                                >
                                    Назад
                                </button>
                                <button
                                    disabled={page >= meta.last_page}
                                    onClick={() =>
                                        setPage((prev) =>
                                            Math.min(meta.last_page, prev + 1)
                                        )
                                    }
                                    className={`px-3 py-1 rounded border ${
                                        page >= meta.last_page
                                            ? "text-gray-400 border-gray-200 cursor-not-allowed"
                                            : "text-gray-700 border-gray-300 hover:bg-gray-100"
                                    }`}
                                >
                                    Далее
                                </button>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <div className="text-gray-500 dark:text-gray-300">
                    История назначений пуста.
                </div>
            )}
        </div>
    );
};

